import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

type AccountRoutingSnapshot = {
  cluster: string | null;
  isRecoverPartner: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class AccountRedirectListenerService {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);

  private _previousSnapshot: AccountRoutingSnapshot | null = null;

  constructor() {
    effect(() => {
      const account = this._authService.accountDto();
      const currentSnapshot: AccountRoutingSnapshot | null = account
        ? {
          cluster: account.companyDto?.cluster ?? null,
          isRecoverPartner: Boolean(account.companyDto?.isRecoverPartner),
        }
        : null;

      if (!currentSnapshot) {
        this._previousSnapshot = null;
        return;
      }

      if (account && account.blocked) {
        this._authService.logout(() => {
          this._router.navigate(['/login']).then();
        });
        return;
      }

      if (!this._previousSnapshot) {
        this._previousSnapshot = currentSnapshot;
        return;
      }

      const previousSnapshot = this._previousSnapshot;
      const hadCluster = previousSnapshot.cluster !== null;
      const hasCluster = currentSnapshot.cluster !== null;

      this._previousSnapshot = currentSnapshot;

      if (!hadCluster && hasCluster) {
        this._navigateIfNeeded('/eis-signals');
        return;
      }

      if (hadCluster && !hasCluster) {
        this._navigateIfNeeded('/');
        return;
      }

      if (previousSnapshot.isRecoverPartner && !currentSnapshot.isRecoverPartner) {
        this._navigateIfNeeded('/');
        return;
      }

      if (!previousSnapshot.isRecoverPartner && currentSnapshot.isRecoverPartner) {
        this._navigateIfNeeded('/eis-dashboard');
      }
    });
  }

  private _navigateIfNeeded(nextRoute: string): void {
    if (this._isApproveChangeAccountFlow()) {
      return;
    }

    if (this._router.url !== nextRoute) {
      this._router.navigate([nextRoute]).then();
    }
  }

  private _isApproveChangeAccountFlow(): boolean {
    return this._router.url.startsWith('/approve-change-account');
  }
}
